const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  avatarUrl?: string;
  phone?: string;
  balance?: number;
}

export interface LoginResponse {
  success: boolean;
  data?: {
    user: User;
    accessToken: string;
    sessionId: string;
    csrfToken: string;
  };
  mfaRequired?: boolean;
  mfaToken?: string;
  error?: string;
}

export const authService = {
  // ============================================================
  // TOKEN MANAGEMENT
  // ============================================================
  
  getToken: () => localStorage.getItem('accessToken'),
  setToken: (token: string) => localStorage.setItem('accessToken', token),
  removeToken: () => localStorage.removeItem('accessToken'),
  
  getUser: (): User | null => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
  
  setUser: (user: User) => localStorage.setItem('user', JSON.stringify(user)),
  removeUser: () => localStorage.removeItem('user'),
  
  getSessionId: () => localStorage.getItem('sessionId'),
  setSessionId: (sessionId: string) => localStorage.setItem('sessionId', sessionId),
  removeSessionId: () => localStorage.removeItem('sessionId'),
  
  getCsrfToken: () => localStorage.getItem('csrfToken'),
  setCsrfToken: (csrfToken: string) => localStorage.setItem('csrfToken', csrfToken),
  removeCsrfToken: () => localStorage.removeItem('csrfToken'),

  // ============================================================
  // LOGIN
  // ============================================================
  
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });

    const result = await response.json();

    if (!response.ok) {
      if (result.mfaRequired) {
        return {
          success: false,
          mfaRequired: true,
          mfaToken: result.data?.mfaToken,
        };
      }
      throw new Error(result.error || 'Error al iniciar sesión');
    }

    if (result.data?.accessToken) {
      this.setToken(result.data.accessToken);
      this.setSessionId(result.data.sessionId);
      this.setCsrfToken(result.data.csrfToken);
      this.setUser(result.data.user);
    }

    return result;
  },

  // ============================================================
  // MFA VERIFICATION
  // ============================================================
  
  async verifyMFA(mfaToken: string, code: string): Promise<LoginResponse> {
    const response = await fetch(`${API_URL}/auth/mfa/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mfaToken, code }),
      credentials: 'include',
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Error al verificar MFA');
    }

    if (result.data?.accessToken) {
      this.setToken(result.data.accessToken);
      this.setSessionId(result.data.sessionId);
      this.setCsrfToken(result.data.csrfToken);
      this.setUser(result.data.user);
    }

    return result;
  },

  // ============================================================
  // FORGOT PASSWORD
  // ============================================================
  
  async forgotPassword(email: string): Promise<void> {
    const response = await fetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Error al enviar el enlace de recuperación');
    }

    return result;
  },

  // ============================================================
  // RESET PASSWORD
  // ============================================================
  
  async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, newPassword }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Error al restablecer la contraseña');
    }

    return result;
  },

  // ============================================================
  // VERIFY TOKEN
  // ============================================================
  
  async verifyToken(): Promise<User | null> {
    const token = this.getToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Session-Id': this.getSessionId() || '',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.clearSession();
        }
        return null;
      }

      const result = await response.json();
      if (result.data) {
        this.setUser(result.data);
        return result.data;
      }
      return null;
    } catch (error) {
      console.error('Error verificando token:', error);
      return null;
    }
  },

  // ============================================================
  // LOGOUT
  // ============================================================
  
  async logout(): Promise<void> {
    const token = this.getToken();
    const sessionId = this.getSessionId();

    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Session-Id': sessionId || '',
        },
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error en logout:', error);
    }

    this.clearSession();
  },

  // ============================================================
  // CLEAR SESSION
  // ============================================================
  
  clearSession(): void {
    this.removeToken();
    this.removeUser();
    this.removeSessionId();
    this.removeCsrfToken();
  },

  // ============================================================
  // FETCH WITH AUTH
  // ============================================================
  
  fetchWithAuth: async (url: string, options: RequestInit = {}) => {
    const token = authService.getToken();
    const sessionId = authService.getSessionId();
    const csrfToken = authService.getCsrfToken();

    // Construir URL absoluta si el path es relativo
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    };

    // Merge con headers existentes
    if (options.headers) {
      Object.assign(headers, options.headers);
    }
    
    const response = await fetch(fullUrl, {
      ...options,
      headers,
      credentials: 'include',
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        authService.clearSession();
        // No redirigir automáticamente, dejar que el componente maneje
      }
      const error = await response.json().catch(() => ({ error: 'Error en la petición' }));
      throw new Error(error.error || `Error ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },
};